import { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Loader2 } from "lucide-react";
import {
  getPlanUsage,
  type PlanUsageItem,
  type PlanUsageListResponse,
} from "@/services/plansService";
import { PENDING_PLAN_LABEL, formatPlanInteger } from "@/utils/planDisplay";
import { cn } from "@/lib/utils";

interface ClientConsumptionTableProps {
  month: number;
  year: number;
  clientId?: string | null;
  search?: string;
}

type RankedClient = PlanUsageItem & {
  usedCalls: number;
  usedTokens: number;
  callLimit: number;
  tokenLimit: number;
  callsPct: number;
  tokensPct: number;
  criticality: number;
};

function isListResponse(
  data: PlanUsageItem | PlanUsageListResponse,
): data is PlanUsageListResponse {
  return "clients" in data;
}

function usageRatio(used: number, limit: number): number {
  if (!limit || limit <= 0) return used > 0 ? 999 : 0;
  return (used / limit) * 100;
}

function formatTokens(value: number): string {
  if (value >= 1_000_000) {
    const m = value / 1_000_000;
    return `${m.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}M`;
  }
  if (value >= 1_000) {
    return `${(value / 1_000).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}K`;
  }
  return formatPlanInteger(value);
}

function formatExcess(value: number): string {
  return Number(value || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

function UsageBar({
  used,
  limit,
  pct,
}: {
  used: number;
  limit: number;
  pct: number;
}) {
  const over = pct > 100;
  const width = Math.min(100, Math.max(0, pct));
  return (
    <div className="space-y-1 min-w-[140px]">
      <div className="text-xs font-medium tabular-nums">
        {formatTokens(used)} / {formatTokens(limit)} — {Math.round(pct)}%
      </div>
      <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
        <div
          className={cn(
            "h-full rounded-full transition-all",
            over ? "bg-destructive" : pct >= 80 ? "bg-amber-500" : "bg-primary",
          )}
          style={{ width: `${width}%` }}
        />
      </div>
    </div>
  );
}

export function ClientConsumptionTable({
  month,
  year,
  clientId,
  search = "",
}: ClientConsumptionTableProps) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<PlanUsageItem[]>([]);
  const [emptyMessage, setEmptyMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setEmptyMessage(null);
      try {
        const response = await getPlanUsage({ month, year });
        if (cancelled) return;

        if (!response.success || !response.data) {
          setRows([]);
          setEmptyMessage("Sem dados de consumo no período selecionado.");
          return;
        }

        const data = response.data;
        if (isListResponse(data)) {
          setRows(data.clients || []);
          if (!data.clients?.length) {
            setEmptyMessage("Sem dados de consumo no período selecionado.");
          }
          return;
        }

        setRows([data]);
      } catch {
        if (!cancelled) {
          setRows([]);
          setEmptyMessage("Sem dados de consumo no período selecionado.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [month, year]);

  const ranked = useMemo(() => {
    const searchTerm = search.trim().toLowerCase();
    const mapped: RankedClient[] = rows
      .filter((item) => {
        if (clientId && clientId !== "all" && clientId !== "null") {
          if (item.client_id !== clientId) return false;
        }
        if (clientId === "null") return false;
        if (!searchTerm) return true;
        const label = `${item.client.company || ""} ${item.client.name || ""}`.toLowerCase();
        return label.includes(searchTerm);
      })
      .map((item) => {
        const usedCalls = item.in_plan_calls + item.excess_calls;
        const usedTokens = item.in_plan_tokens + item.excess_tokens;
        const callLimit = Number(item.plan?.call_limit || 0);
        const tokenLimit = Number(item.plan?.token_limit || 0);
        const callsPct = usageRatio(usedCalls, callLimit);
        const tokensPct = usageRatio(usedTokens, tokenLimit);
        const criticality = Math.max(callsPct, tokensPct);
        return {
          ...item,
          usedCalls,
          usedTokens,
          callLimit,
          tokenLimit,
          callsPct,
          tokensPct,
          criticality,
        };
      })
      .sort((a, b) => b.criticality - a.criticality);

    return mapped;
  }, [rows, clientId, search]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Consumo por cliente</CardTitle>
        <CardDescription>
          Ordenado por criticidade (maior uso relativo no topo)
        </CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="flex items-center justify-center py-16 gap-2 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
            Carregando consumo...
          </div>
        ) : ranked.length === 0 ? (
          <div className="py-12 text-center text-muted-foreground px-4">
            {emptyMessage || "Sem dados de consumo no período selecionado."}
          </div>
        ) : (
          <div className="rounded-md border-t overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Clientes</TableHead>
                  <TableHead>Plano</TableHead>
                  <TableHead>Acionamentos</TableHead>
                  <TableHead>Tokens</TableHead>
                  <TableHead className="text-right">Excedente</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ranked.map((row) => (
                  <TableRow key={row.client_id}>
                    <TableCell className="font-medium">
                      {row.client.company || row.client.name || "Cliente"}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="secondary"
                        className="bg-primary/10 text-primary border-0"
                      >
                        {row.plan?.name || PENDING_PLAN_LABEL}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <UsageBar
                        used={row.usedCalls}
                        limit={row.callLimit}
                        pct={row.callsPct}
                      />
                    </TableCell>
                    <TableCell>
                      <UsageBar
                        used={row.usedTokens}
                        limit={row.tokenLimit}
                        pct={row.tokensPct}
                      />
                    </TableCell>
                    <TableCell className="text-right tabular-nums font-medium">
                      {formatExcess(row.excess_cost)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
